import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import type { Repository } from '../types/api';

interface UseRepositoriesOptions {
  autoRefresh?: boolean;
  refreshInterval?: number;
}

interface UseRepositoriesReturn {
  repositories: Repository[];
  loading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
  syncRepository: (fullName: string) => Promise<void>;
  updateTags: (fullName: string, tags: string[]) => Promise<void>;
}

export function useRepositories(options: UseRepositoriesOptions = {}): UseRepositoriesReturn {
  const { autoRefresh = false, refreshInterval = 30000 } = options;

  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchRepositories = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiService.getRepositories();
      setRepositories(data);
    } catch (err) {
      setError(err as Error);
      console.error('Failed to fetch repositories:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    await fetchRepositories();
  }, [fetchRepositories]);

  const syncRepository = useCallback(async (fullName: string) => {
    try {
      await apiService.syncRepository(fullName);
      await fetchRepositories();
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchRepositories]);

  const updateTags = useCallback(async (fullName: string, tags: string[]) => {
    try {
      await apiService.updateRepositoryTags(fullName, tags);
      await fetchRepositories();
    } catch (err) {
      setError(err as Error);
      throw err;
    }
  }, [fetchRepositories]);

  useEffect(() => {
    fetchRepositories();
  }, [fetchRepositories]);

  useEffect(() => {
    if (autoRefresh && refreshInterval > 0) {
      const interval = setInterval(fetchRepositories, refreshInterval);
      return () => clearInterval(interval);
    }
  }, [autoRefresh, refreshInterval, fetchRepositories]);

  return {
    repositories,
    loading,
    error,
    refresh,
    syncRepository,
    updateTags,
  };
}
